import { CSSProperties, forwardRef, ReactNode, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ReferenceManyField } from "../fields/ReferenceManyField";
import {
  CreateBase,
  EditBase,
  Identifier,
  RecordContextProvider,
  SaveButton,
  useGetMany,
  SimpleForm,
  TextInput,
  useGetOne,
  useGetRecordId,
  useInput,
  useListContext,
  useRecordContext,
} from "react-admin";
import { DateField } from "../fields/DateField";
import { type DrivingSchoolNote, isDrivingSchoolNote, StudentNote } from "../model/Note";
import {
  Avatar,
  Box,
  Button,
  Chip,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Spinner } from "../misc/Spinner";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { gcs } from "../utils/storage";
import { reportError } from "../backoffice.utils";
import { useWatch } from "react-hook-form";
import { useQueryClient } from "react-query";
import { autovioColors } from "../misc/backofficeTheme";
import { type User, usersProvider } from "../providers/usersProvider";
import type { Note, NotesSource } from "../providers/notesProvider.js";
import { useDropzone } from "react-dropzone";
import { Row } from "../misc/Row";
import { Column } from "../misc/Column";
import { currentUser, grants } from "../backoffice.access_control";
import FirestoreIcon from "@mui/icons-material/LocalFireDepartment";
import { openInFirestoreConsole } from "../firebase";
import { useThemeColor } from "../hooks/useThemeColor";
import { studentNotesProvider, drivingSchoolNotesProvider } from "../providers/notesProvider";
import { DeleteIcon } from "../icons/DeleteIcon";
import { useMenu } from "../hooks/useMenu";
import { basenameWithoutExtension } from "../utils/basenameWithoutExtension";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { openDownloadWindow } from "../utils/openDownloadWindow";

export const NotesList = forwardRef(
  (
    {
      title,
      style,
      resource,
      showSearchField,
      searchQuery,
      onSearchQueryChange,
    }: {
      title?: ReactNode;
      style?: CSSProperties;
      resource: NotesSource;
      showSearchField?: boolean;
      searchQuery?: string;
      onSearchQueryChange?: (value: string) => void;
    },
    ref,
  ) => {
    const recordId = useGetRecordId();
    return (
      <div style={{ position: "relative", ...style }}>
        <ReferenceManyField
          label=""
          resource={resource}
          reference={resource}
          target={resource === "studentNotes" ? "studentUid" : "drivingSchoolUid"}
          sort={{ field: "createdAt", order: "DESC" }}
          // Filter out notes without a body ...
          filter={{ body: (it: StudentNote | DrivingSchoolNote) => !!it }}
        >
          <_NotesList
            resource={resource}
            recordId={recordId}
            title={title}
            showSearchField={showSearchField}
            searchQuery={searchQuery}
            onSearchQueryChange={onSearchQueryChange}
            ref={ref}
          />
        </ReferenceManyField>
      </div>
    );
  },
);

const _NotesList = forwardRef(
  (
    {
      recordId,
      title,
      resource,
      showSearchField,
      searchQuery,
      onSearchQueryChange,
    }: {
      recordId: Identifier;
      title?: ReactNode;
      resource: NotesSource;
      showSearchField?: boolean;
      searchQuery?: string;
      onSearchQueryChange?: (value: string) => void;
    },
    ref,
  ) => {
    const { data, isLoading, total, refetch } = useListContext<StudentNote | DrivingSchoolNote>();
    const [state, setState] = useState<"show button" | "show form">("show button");
    const normalizedSearchQuery = (searchQuery ?? "").trim().toLocaleLowerCase();
    const hasSearchQuery = normalizedSearchQuery.length > 0;
    const needsAuthorLookup = resource === "drivingSchoolNotes" && showSearchField && hasSearchQuery;
    const authorIds = useMemo(
      () =>
        needsAuthorLookup
          ? Array.from(
              new Set(
                (data ?? [])
                  .map((note) => (isDrivingSchoolNote(note) ? note.createdById : note.createdByUid) ?? "")
                  .filter((id) => !!id),
              ),
            )
          : [],
      [data, needsAuthorLookup],
    );
    const { data: authors } = useGetMany<User>(
      "users",
      { ids: authorIds },
      { enabled: needsAuthorLookup && authorIds.length > 0 },
    );
    const authorNameById = useMemo(
      () => new Map((authors ?? []).map((author) => [author.id, author.name ?? ""])),
      [authors],
    );
    const filteredData =
      data?.filter((note) => {
        if (!hasSearchQuery) {
          return true;
        }
        const authorId = (isDrivingSchoolNote(note) ? note.createdById : note.createdByUid) ?? "";
        const authorName = isDrivingSchoolNote(note)
          ? (authorNameById.get(authorId) ?? "")
          : (note.createdByName ?? authorNameById.get(authorId) ?? "");
        const searchableText = [
          note.body ?? "",
          authorName,
          ...(note.attachments?.map((attachment) => basenameWithoutExtension(attachment.name)) ?? []),
        ]
          .join(" ")
          .toLocaleLowerCase();
        return searchableText.includes(normalizedSearchQuery);
      }) ?? [];

    useImperativeHandle(ref, function () {
      return {
        showForm: () => setState("show form"),
      };
    });

    const titleAndButton = state === "show button" && (
      <div style={{ display: "flex", justifyContent: "space-between" }}>{title ?? <div />}</div>
    );
    const titleAndForm = state === "show form" && (
      <>
        {title}
        <_AddNoteForm resource={resource} recordId={recordId} close={() => setState("show button")} />
      </>
    );
    const searchField = showSearchField && (
      <TextField
        autoFocus
        fullWidth
        size="small"
        placeholder="Notizen durchsuchen"
        value={searchQuery ?? ""}
        onChange={(event) => onSearchQueryChange?.(event.target.value)}
        sx={{ mt: state === "show form" ? 1.5 : -0.5, mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchQuery ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => onSearchQueryChange?.("")}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />
    );

    if (isLoading) {
      return (
        <>
          {title}
          {searchField}
          <div style={{ flex: 1 }}>
            <Spinner style={{ margin: "20px" }} />
          </div>
        </>
      );
    }

    if (!data || data.length === 0 || total === 0) {
      return (
        <>
          {titleAndButton || titleAndForm}
          {searchField}
          {!titleAndForm && (
            <Typography
              variant="body2"
              sx={{
                height: "20px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              Keine Notizen vorhanden.
            </Typography>
          )}
        </>
      );
    }

    if (filteredData.length === 0) {
      return (
        <>
          {titleAndButton || titleAndForm}
          {searchField}
          <Typography variant="body2">Keine passenden Notizen gefunden.</Typography>
        </>
      );
    }

    return (
      <>
        {titleAndButton || titleAndForm}
        {searchField}
        <List sx={{ paddingTop: 0, mt: state === "show form" && !showSearchField ? 2 : 0 }}>
          {filteredData.map((note) => (
            <RecordContextProvider key={note.id} value={note}>
              <_NotesListItem resource={resource} refetch={refetch} />
            </RecordContextProvider>
          ))}
        </List>
      </>
    );
  },
);

function useCreatedBy(note: StudentNote | DrivingSchoolNote): User | undefined {
  const id = (isDrivingSchoolNote(note) ? note.createdById : note.createdByUid) ?? "";
  const { data: author } = useGetOne<User>("users", { id }, { enabled: !!id });
  if (id) {
    return author ?? { id, name: "", avatarUrl: usersProvider.getAvatarUrl(id) };
  }
}

function useUpdatedBy(note: StudentNote | DrivingSchoolNote): User | undefined {
  const id = (isDrivingSchoolNote(note) ? note.updatedById : note.updatedByUid) ?? "";
  const { data: editor } = useGetOne<User>("users", { id }, { enabled: !!id });
  if (id) {
    return editor ?? { id, name: "", avatarUrl: usersProvider.getAvatarUrl(id) };
  }
}

function useDeletedBy(note: StudentNote | DrivingSchoolNote): User | undefined {
  const { data } = useGetOne<User>("users", { id: note.deletedById ?? "" }, { enabled: !!note.deletedById });
  return data;
}

function _NotesListItem({ resource, refetch }: { resource: NotesSource; refetch: () => void }) {
  const note = useRecordContext<Note>();
  const author = useCreatedBy(note);
  const editor = useUpdatedBy(note);
  const deletor = useDeletedBy(note);
  const [showActions, setShowActions] = useState(false);
  const [mode, setMode] = useState<"show" | "edit">("show");
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  if (mode === "edit") {
    return <_EditNoteForm resource={resource} note={note} close={() => setMode("show")} />;
  }

  const deleteNote = async () => {
    try {
      await (resource === "studentNotes"
        ? studentNotesProvider.delete("", { id: note.id, meta: { note } })
        : drivingSchoolNotesProvider.delete("", { id: note.id, meta: { note } }));
      refetch();
      showSuccessToast("Notiz erfolgreich gelöscht.");
    } catch (error) {
      showErrorToast("Fehler beim Löschen der Notiz.");
      reportError(`Failed to delete note ${note.id}`, error);
    }
  };

  const canDelete = grants.has("Note:delete") || (author && author.id === currentUser?.uid);

  return (
    <div
      style={{ position: "relative", marginBottom: 30 }}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <ListItem disablePadding>
        <Row spacing={1} sx={{ opacity: note.deleted ? 0.7 : 1 }}>
          <Avatar src={author?.avatarUrl} sx={{ width: 20, height: 20, marginTop: 0.5 }} />
          <Column>
            <Box pr={2} mb={0.5} sx={{ lineHeight: "14px" }}>
              <DateField
                className="RaLabeled-label"
                source="createdAt"
                showTime
                sx={{
                  fontWeight: "bold",
                  lineHeight: "14px",
                  fontSize: "14px !important",
                  textDecoration: note.deleted ? "line-through" : "none",
                }}
              />
              {author?.name && (
                <Box
                  component="span"
                  className="RaLabeled-label"
                  sx={{ color: "text.secondary", fontSize: "12px", lineHeight: "14px" }}
                >{` ${author.name}`}</Box>
              )}
              {note.updatedAt && note.updatedAt > note.createdAt && (
                <>
                  <Box component="span" className="RaLabeled-label" sx={{ color: "text.secondary", fontSize: 12 }}>
                    {" (geändert: "}
                  </Box>
                  <DateField
                    className="RaLabeled-label"
                    source="updatedAt"
                    showTime
                    sx={{ color: "text.secondary", fontSize: "12px !important" }}
                  />
                  <Box component="span" className="RaLabeled-label" sx={{ color: "text.secondary", fontSize: 12 }}>
                    {editor?.name ? `, ${editor.name})` : ")"}
                  </Box>
                </>
              )}
              {(showActions || open) && (
                <>
                  {!note.deleted && (
                    <>
                      <IconButton onClick={openMenu} sx={{ position: "absolute", right: 0, top: "-10px" }}>
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={closeMenu}
                        sx={(theme) => ({ zIndex: theme.zIndex.modal + 2 })}
                        transformOrigin={{ vertical: "top", horizontal: "center" }}
                      >
                        <MenuItem
                          onClick={() => {
                            setMode("edit");
                            closeMenu();
                          }}
                        >
                          Bearbeiten
                        </MenuItem>
                        {canDelete && (
                          <MenuItem
                            onClick={() => {
                              closeMenu();
                              void deleteNote();
                            }}
                          >
                            Löschen
                          </MenuItem>
                        )}
                      </Menu>
                    </>
                  )}
                  {grants.has("viewFirestore") && (
                    <IconButton
                      sx={{ position: "absolute", top: "-10px", right: note.deleted ? 0 : "36px" }}
                      onClick={() =>
                        openInFirestoreConsole(
                          isDrivingSchoolNote(note)
                            ? `/driving_schools/${note.drivingSchoolId}/notes/${note.id}`
                            : `/users/${note.studentUid}/notes/${note.id}`,
                        )
                      }
                    >
                      <FirestoreIcon />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
            <_NoteText>{note.body ?? ""}</_NoteText>
            <Box sx={{ display: "flexWrap", mt: 0.75 }}>
              {(note.attachments ?? []).map((it) => {
                const fileName = basenameWithoutExtension(it.name);
                return (
                  <Chip
                    key={it.id}
                    sx={{
                      marginTop: "3px",
                      marginRight: "3px",
                      height: 24,
                      fontSize: "10px",
                      fontWeight: 500,
                      color: "text.secondary",
                      backgroundColor: "action.hover",
                      maxWidth: {
                        xs: "calc(100vw - 160px)",
                        md: "calc(((100vw - 360px) / 3) - 110px)",
                      },
                      "& .MuiChip-label": {
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      },
                    }}
                    onClick={async () => {
                      const attachmentWindow = openDownloadWindow();
                      try {
                        const downloadUrl = await gcs.getDownloadUrl(it.path);
                        attachmentWindow.navigate(downloadUrl);
                      } catch (error) {
                        attachmentWindow.close();
                        showErrorToast("Fehler beim Öffnen der angehängten Datei.");
                        reportError(`Failed to get download URL for attachment ${it.id}`, error);
                      }
                    }}
                    icon={<AttachFileIcon sx={{ fontSize: 14, color: "text.secondary" }} />}
                    label={
                      fileName.length >= 24 ? (
                        <Tooltip title={fileName}>
                          <span>{`${fileName.slice(0, 24)}...`}</span>
                        </Tooltip>
                      ) : (
                        fileName
                      )
                    }
                  />
                );
              })}
            </Box>
            {note.deleted && note.deletedAt && deletor && (
              <Row color={autovioColors.red} fontSize="16px" gap={0.5} alignItems="center" mt={0.25}>
                <DeleteIcon fontSize="inherit" />
                <Typography variant="body2">
                  Gelöscht am {note.deletedAt.toFormat("d.M.yyyy")} von {deletor.name}
                </Typography>
              </Row>
            )}
          </Column>
        </Row>
      </ListItem>
    </div>
  );
}

function _NoteText({ children: text, maxLines = 5 }: { children: string; maxLines?: number }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const primaryColor = useThemeColor("primary");

  useEffect(() => {
    const checkIfTruncated = () => {
      const textElement = textRef.current;
      if (textElement) {
        const lineHeight = parseFloat(window.getComputedStyle(textElement).lineHeight);
        const maxHeight = lineHeight * maxLines + 1;
        setIsTruncated(textElement.scrollHeight > maxHeight);
      }
    };

    checkIfTruncated();

    window.addEventListener("resize", checkIfTruncated);
    return () => window.removeEventListener("resize", checkIfTruncated);
  }, [text, maxLines]);

  return (
    <>
      <Typography
        ref={textRef}
        sx={{
          maxHeight: isExpanded ? "none" : `${maxLines * 1.2}em`,
          overflow: "hidden",
          lineHeight: "1.2em",
          textOverflow: "ellipsis",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          wordBreak: "break-word",
          fontSize: 14,
          padding: 0,
        }}
      >
        {text}
      </Typography>
      {isTruncated && !isExpanded && (
        <span style={{ color: primaryColor.main, cursor: "pointer", fontSize: 14 }} onClick={() => setIsExpanded(true)}>
          mehr anzeigen
        </span>
      )}
    </>
  );
}

function _AddNoteForm({
  recordId,
  close,
  resource,
}: {
  recordId: Identifier;
  close: () => void;
  resource: NotesSource;
}) {
  const queryClient = useQueryClient();

  return (
    <CreateBase
      record={{ recordId }}
      mutationOptions={{
        onSuccess: async () => {
          await queryClient.invalidateQueries([resource]);
          close();
        },
      }}
    >
      <_NoteForm close={close} />
    </CreateBase>
  );
}

function _EditNoteForm({
  note,
  close,
  resource,
}: {
  note: StudentNote | DrivingSchoolNote;
  close: () => void;
  resource: NotesSource;
}) {
  const queryClient = useQueryClient();
  return (
    <EditBase
      resource={resource}
      id={note.id}
      redirect={false}
      className="edit-note"
      mutationMode="pessimistic"
      sx={{
        marginTop: 0,
        marginBottom: "8px",
        border: "1px solid",
        borderColor: "divider",
      }}
      mutationOptions={{
        onSuccess: async () => {
          await queryClient.invalidateQueries([resource]);
          close();
        },
      }}
    >
      <_NoteForm close={close} />
    </EditBase>
  );
}

function _NoteForm({ close }: { close: () => void }) {
  const ref = useRef<{ openSelectFilesDialog: () => void }>();

  const validate = (values: Record<string, any>) => {
    const errors: { [field: string]: string } = {};
    if (!values.body || values.body.trim().length === 0) {
      errors.body = "Eine Notiz darf nicht leer sein.";
    }
    return errors;
  };

  return (
    <SimpleForm
      sx={{ paddingLeft: 0, paddingRight: 0, "&.MuiCardContent-root": { pt: 0 } }}
      validate={validate}
      toolbar={<_NoteActions openSelectFilesDialog={() => ref.current!.openSelectFilesDialog()} close={close} />}
    >
      <_NoteFormFields ref={ref} />
    </SimpleForm>
  );
}

const _NoteFormFields = forwardRef(function _NoteFormFields(_, ref) {
  // Copied and adapted from react-admin FileInput.tsx ...

  const note = useRecordContext<StudentNote | DrivingSchoolNote>();
  const existingAttachments = note?.attachments ?? [];
  const {
    id,
    field: { onChange, onBlur, value },
  } = useInput({ source: "files" });
  const files = value ? (Array.isArray(value) ? value : [value]) : [];

  const onDrop = (newFiles: any) => {
    onChange([...files, ...newFiles]);
    onBlur();
  };

  const {
    getRootProps,
    getInputProps,
    open: openSelectFilesDialog,
  } = useDropzone({
    multiple: true,
    noClick: true,
    onDrop,
  });

  useImperativeHandle(ref, () => ({ openSelectFilesDialog }), []);

  return (
    <div {...getRootProps()} style={{ width: "100%" }}>
      <input id={id} name={id} {...getInputProps()} />
      <TextInput
        source="body"
        label={false}
        rows={4}
        fullWidth
        multiline
        sx={{
          border: "1px solid",
          borderColor: "divider",
          ".MuiOutlinedInput-notchedOutline": { borderColor: "transparent !important" },
        }}
      />
      {existingAttachments.map((attachment) => (
        <div key={attachment.id} style={{ display: "flex", alignItems: "center", marginTop: "5px" }}>
          <AttachFileIcon style={{ fontSize: 14 }} />
          <Typography
            variant="body2"
            style={{ marginLeft: "6px", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {attachment.name}
          </Typography>
        </div>
      ))}
      {files.map((file: File) => (
        <div key={file.name} style={{ display: "flex", alignItems: "center", marginTop: "5px" }}>
          <AttachFileIcon style={{ fontSize: 14 }} />
          <Typography
            variant="body2"
            style={{ marginLeft: "6px", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {file.name}
          </Typography>
        </div>
      ))}
    </div>
  );
});

function _NoteActions({ openSelectFilesDialog, close }: { openSelectFilesDialog: () => void; close: () => void }) {
  const bodyText = useWatch({ name: "body" });
  const primaryColor = useThemeColor("primary");

  return (
    <Row sx={{ mb: 1 }}>
      <Tooltip title="Dokument(e) anhängen" placement="top">
        <IconButton
          size="small"
          color="primary"
          onClick={openSelectFilesDialog}
          sx={{ border: `1px solid ${primaryColor.main}` }}
        >
          <AttachFileIcon />
        </IconButton>
      </Tooltip>
      <div style={{ flex: 1 }} />
      <Button variant="outlined" size="small" onClick={close} style={{ marginRight: "6px" }}>
        Abbrechen
      </Button>
      <SaveButton icon={<></>} disabled={!bodyText || bodyText.trim().length === 0} />
    </Row>
  );
}
